import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Input, InputNumber, Modal, Select } from 'antd';

import FormDefinitionEditor, {
	blankDefinition,
	definitionFrom,
	staleNameOf,
	useFormDefinition,
} from './FormDefinitionEditor';
import {
	createShareLink,
	updateIntakeLink,
	DEFAULT_EXPIRY_DAYS,
	EXPIRY_OPTIONS,
} from '../../services/shareLinks';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import {
	describeIntakeError,
	DEFAULT_MAX_SUBMISSIONS,
	MAX_MAX_SUBMISSIONS,
} from '../../utils/intakeLinks';

const KEEP_EXPIRY = 'keep';

const blankState = () => ({
	...blankDefinition(),
	name: '',
	maxSubmissions: DEFAULT_MAX_SUBMISSIONS,
	expiresInDays: DEFAULT_EXPIRY_DAYS,
});

// An intake link carries its definition at its top level (keepr-api SCHEMA
// § Intake links); the editor reads it from there.
const stateFromLink = link => ({
	...definitionFrom(link, link?.card_id),
	name: link?.name || '',
	maxSubmissions: Number.isInteger(link?.maxSubmissions) ? link.maxSubmissions : DEFAULT_MAX_SUBMISSIONS,
	expiresInDays: KEEP_EXPIRY,
});

// KPR-360: a NEW link seeded from an invite link a package prepared (its
// draft's `definition`, its cap and expiry) — "Change it first…".
const stateFromTemplate = template => ({
	...definitionFrom(template?.definition, template?.card_id),
	name: template?.name || '',
	maxSubmissions: Number.isInteger(template?.invite?.maxSubmissions) ? template.invite.maxSubmissions : DEFAULT_MAX_SUBMISSIONS,
	expiresInDays: Number.isInteger(template?.invite?.expiresInDays) ? template.invite.expiresInDays : DEFAULT_EXPIRY_DAYS,
});

/**
 * Make, or change, an INTAKE link: a public form over one card that anyone
 * holding the link can fill in, each submission landing as one item held for
 * review (keepr-api docs/SCHEMA.md § Intake links).
 *
 * What the manager decides, top to bottom: which card; which of its fields
 * the form asks for (an explicit allowlist — a field added to the card later
 * is NOT collected by a link made today); what to fill in for the visitor
 * instead of asking; the words the visitor reads; whether they may leave a
 * note; how many submissions the link takes; and when it closes.
 *
 * The API refuses everything this form cannot know is safe, and its refusal
 * is shown in words (utils/intakeLinks describeIntakeError). What this form
 * CAN know — a card that signs its saves or numbers its records, a field that
 * names a person or is calculated, a field that can be required — it shows
 * before the request, disabled with its reason, so the Create button is never
 * the first place a manager hears "no".
 *
 * `link` present = edit (PATCH). The card never changes on an edit — a form
 * for another card is another link. `template` (KPR-360) — an invite link a
 * package prepared — seeds a NEW link with what it asks, its cap and expiry.
 *
 * The definition — card to the after-send message — is FormDefinitionEditor, shared with
 * the permanent public form (KPR-513); what is this kind's own is the
 * warning above it, the name, and Stop after / Expires after below it.
 */
export default function IntakeLinkModal({
	open,
	onClose,
	onSaved,
	collectionId,
	link = null,
	template = null,
	cardOptions = [],
	cardsById = {},
	getElementsForCard = null,
}) {
	const editing = Boolean(link);
	const [state, setState] = useState(blankState);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	// Bumped with every seed: the seeded state is what the unsaved-changes
	// guard measures typing against.
	const [seedVersion, setSeedVersion] = useState(0);
	const model = useFormDefinition({ state, setState, collectionId, cardOptions, cardsById, getElementsForCard });
	const { set, published, cardId, problem, definition, elementsByName, setRefused } = model;

	useEffect(() => {
		if (!open) return;
		setState(editing ? stateFromLink(link) : (template ? stateFromTemplate(template) : blankState()));
		setError(null);
		setSaving(false);
		setRefused([]);
		setSeedVersion(version => version + 1);
	}, [open, editing, link, template, setRefused]);

	const { resetDirty, guardDiscard } = useDirtyForm(state, { enabled: open, baselineKey: seedVersion });
	// Cancel, the ✕, the mask and Esc all ask first when something was typed.
	const handleCancel = useCallback(() => guardDiscard(() => onClose?.()), [guardDiscard, onClose]);

	const ready = Boolean(cardId) && !problem;

	const handleSave = useCallback(async () => {
		if (!ready) return;
		setSaving(true);
		setError(null);
		const config = {
			...definition,
			maxSubmissions: state.maxSubmissions,
			name: state.name.trim() || undefined,
			expiresInDays: state.expiresInDays === KEEP_EXPIRY ? undefined : state.expiresInDays,
		};
		try {
			const result = editing
				? await updateIntakeLink(collectionId, normalizeId(link._id), config)
				: await createShareLink(collectionId, { kind: 'intake', card_id: cardId, ...config });
			resetDirty();
			onSaved?.(result, { created: !editing });
		} catch (err) {
			const staleName = staleNameOf(err);
			if (staleName) setRefused(prev => (prev.includes(staleName) ? prev : [...prev, staleName]));
			setError(describeIntakeError(err, elementsByName));
		} finally {
			setSaving(false);
		}
	}, [ready, definition, state, editing, collectionId, link, cardId, onSaved, elementsByName, resetDirty, setRefused]);

	const expiryOptions = editing
		? [
			{ value: KEEP_EXPIRY, label: link?.expiresAt ? `Keep — ${formatDisplayDate(link.expiresAt)}` : 'Keep the current expiry' },
			...EXPIRY_OPTIONS.map(option => ({ ...option, label: `${option.label} from today` })),
		]
		// A prepared link's own lifetime (KPR-360) when it is none of the usual ones.
		: [
			...(Number.isInteger(state.expiresInDays) && !EXPIRY_OPTIONS.some(option => option.value === state.expiresInDays)
				? [{ value: state.expiresInDays, label: `${state.expiresInDays} days` }]
				: []),
			...EXPIRY_OPTIONS,
		];

	return (
		<Modal
			open={open}
			title={editing ? 'Edit intake link' : 'New intake link'}
			okText={editing ? 'Save changes' : 'Create link'}
			okButtonProps={{ disabled: !ready, 'data-action': 'intake-save' }}
			confirmLoading={saving}
			onOk={handleSave}
			onCancel={handleCancel}
			destroyOnClose
			width={620}
			className="share-links__modal intake-link-modal"
		>
			<Alert
				className="intake-link-modal__warning"
				type="warning"
				showIcon
				message="Anyone with this link can add an item"
				description={(
					<>
						No account, no sign-in. Each submission waits in this collection’s review queue — out of the
						list, counts, exports and public views — until someone accepts it.
						{published ? (
							<span className="intake-link-modal__published" data-testid="intake-link-published">
								{' '}This collection is published: once accepted, a submission is read by everyone who uses
								keepr. Ask for names and contact details privately.
							</span>
						) : null}
					</>
				)}
			/>

			<FormDefinitionEditor
				definition={model}
				editing={editing}
				noun="link"
				idPrefix="intake-link"
				onCardChosen={() => setError(null)}
				nameField={(
					<div className="share-links__field">
						<label className="share-links__field-label" htmlFor="intake-link-name">Name (optional)</label>
						<Input
							id="intake-link-name"
							maxLength={100}
							value={state.name}
							placeholder="e.g. Vendor inspections"
							onChange={event => set({ name: event.target.value })}
						/>
						<p className="share-links__hint">For you and your team: it names the link in this list and on every item it brings in.</p>
					</div>
				)}
			/>

			<div className="share-links__field intake-link-modal__row">
				<div>
					<label className="share-links__field-label" htmlFor="intake-link-cap">Stop after</label>
					<InputNumber
						id="intake-link-cap"
						min={1}
						max={MAX_MAX_SUBMISSIONS}
						precision={0}
						value={state.maxSubmissions}
						onChange={value => set({ maxSubmissions: Number.isInteger(value) ? value : DEFAULT_MAX_SUBMISSIONS })}
						addonAfter="submissions"
					/>
				</div>
				<div>
					<label className="share-links__field-label" htmlFor="intake-link-expiry">
						{editing ? 'Expiry' : 'Expires after'}
					</label>
					<Select
						id="intake-link-expiry"
						className="share-links__expiry-select"
						value={state.expiresInDays}
						options={expiryOptions}
						onChange={value => set({ expiresInDays: value })}
					/>
				</div>
			</div>

			{problem && cardId ? <p className="share-links__hint intake-link-modal__problem" data-testid="intake-link-problem">{problem}</p> : null}
			{error ? <Alert className="intake-link-modal__error" type="error" showIcon message={error} data-testid="intake-link-error" /> : null}
		</Modal>
	);
}
